import React, { useState, useEffect, useMemo } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const STATUS_OPTIONS = [
  { value: "IP", label: "In Process" },
  { value: "IT", label: "In Process & Transfer" },
  { value: "CT", label: "Close & Transfer" },
  { value: "CL", label: "Closed" },
];

const MAX_DOC_BYTES = 200 * 1024;
const ALLOWED_DOC_EXTS = ["jpg", "jpeg", "png", "pdf"];

// Re-encodes an oversized JPG/PNG as JPEG, shrinking quality then dimensions until it fits under maxBytes.
function compressImageFile(file, maxBytes) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the selected image."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not read the selected image."));
      img.onload = async () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          let width = img.naturalWidth;
          let height = img.naturalHeight;

          const renderBlob = (w, h, quality) => new Promise((res) => {
            canvas.width = w;
            canvas.height = h;
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            canvas.toBlob(res, "image/jpeg", quality);
          });

          let quality = 0.9;
          let blob = await renderBlob(width, height, quality);

          while (blob && blob.size > maxBytes && quality > 0.3) {
            quality -= 0.1;
            blob = await renderBlob(width, height, quality);
          }

          while (blob && blob.size > maxBytes && (width > 400 || height > 400)) {
            width = Math.round(width * 0.85);
            height = Math.round(height * 0.85);
            blob = await renderBlob(width, height, quality);
          }

          if (!blob || blob.size > maxBytes) {
            reject(new Error("Could not reduce this image below 200 KB. Please choose a smaller image."));
            return;
          }

          const baseName = file.name.replace(/\.[^.]+$/, "");
          resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
        } catch {
          reject(new Error("Failed to process the selected image."));
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ResolverAction() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const empId    = parseInt(localUser?.id,       10);

  const [pendingTickets,  setPendingTickets]  = useState([]);
  const [progressTypes,   setProgressTypes]   = useState([]);
  const [resolverMasters, setResolverMasters] = useState([]);

  const [form, setForm] = useState({
    ticketId:      "",
    progressId:    "",
    resolverId:    "",
    resolverEmpId: "",
    status:        "IP",
    remarks:       "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error,   setError]   = useState("");

  const [docFile, setDocFile] = useState(null);
  const [docInfo, setDocInfo] = useState("");
  const [docError, setDocError] = useState("");
  const [docProcessing, setDocProcessing] = useState(false);

  const loadTickets = () =>
    fetch(`${API_ROOT}/api/complaints/resolver-pending-tickets?moduleId=${moduleId}&empId=${empId}`)
      .then(r => r.json())
      .then(d => {
        const list = d.data || [];
        setPendingTickets(list);
        if (list.length) setForm(prev => ({ ...prev, ticketId: String(list[0].TicketID) }));
      })
      .catch(() => {});

  useEffect(() => {
    if (moduleId !== 9) return;
    const base = `${API_ROOT}/api/complaints`;

    loadTickets();

    fetch(`${base}/progress-types?moduleId=${moduleId}`)
      .then(r => r.json())
      .then(d => {
        const list = d.data || [];
        setProgressTypes(list);
        if (list.length) setForm(prev => ({ ...prev, progressId: String(list[0].ProgressID) }));
      })
      .catch(() => {});

    fetch(`${base}/resolver-masters?moduleId=${moduleId}`)
      .then(r => r.json())
      .then(d => {
        const list = d.data || [];
        setResolverMasters(list);
        if (list.length) setForm(prev => ({ ...prev, resolverId: String(list[0].ResolverID) }));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch employee ID whenever resolverId changes
  useEffect(() => {
    if (!form.resolverId || moduleId !== 9) return;
    fetch(`${API_ROOT}/api/complaints/resolver-empid?moduleId=${moduleId}&resolverId=${form.resolverId}`)
      .then(r => r.json())
      .then(d => setForm(prev => ({ ...prev, resolverEmpId: d.employeeId ? String(d.employeeId) : "" })))
      .catch(() => {});
  }, [form.resolverId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onDocChange = async (e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    setDocError(""); setDocFile(null); setDocInfo("");
    if (!file) return;

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_DOC_EXTS.includes(ext)) {
      setDocError("Only JPG, JPEG, PNG, or PDF files are allowed.");
      return;
    }

    if (ext === "pdf") {
      if (file.size > MAX_DOC_BYTES) {
        setDocError(`PDF is ${(file.size / 1024).toFixed(0)} KB. PDFs can't be auto-reduced — please compress/re-scan it under 200 KB and re-upload.`);
        return;
      }
      setDocFile(file);
      setDocInfo(`${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
      return;
    }

    if (file.size <= MAX_DOC_BYTES) {
      setDocFile(file);
      setDocInfo(`${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
      return;
    }

    setDocProcessing(true);
    try {
      const reduced = await compressImageFile(file, MAX_DOC_BYTES);
      setDocFile(reduced);
      setDocInfo(`${reduced.name} (${(reduced.size / 1024).toFixed(0)} KB, reduced from ${(file.size / 1024).toFixed(0)} KB)`);
    } catch (err) {
      setDocError(err.message || "Failed to process the selected image.");
    } finally {
      setDocProcessing(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setMessage(""); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("moduleId", moduleId);
      fd.append("empId", empId);
      fd.append("ticketId", form.ticketId);
      fd.append("progressId", form.progressId);
      fd.append("resolverId", form.resolverId);
      fd.append("resolverEmpId", form.resolverEmpId);
      fd.append("status", form.status);
      fd.append("remarks", form.remarks);
      if (docFile) fd.append("SupportingDoc", docFile);

      const res = await fetch(`${API_ROOT}/api/complaints/resolver-action`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save.");
      setMessage(data.message || "Record Updated successfully");
      setForm(prev => ({ ...prev, remarks: "" }));
      setDocFile(null); setDocInfo(""); setDocError("");
      loadTickets();
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  if (moduleId !== 9) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <h3 className="ctitle">Pending Complaints</h3>

      {error   && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <form onSubmit={onSubmit}>
        <div className="col-lg-10">

          <div className="mb-2">
            <label className="form-label">Complaint Category</label>
            <select
              className="form-select"
              name="ticketId"
              value={form.ticketId}
              onChange={onChange}
              required
            >
              {pendingTickets.length === 0 && (
                <option value="">No pending complaints</option>
              )}
              {pendingTickets.map(t => (
                <option key={t.TicketID} value={String(t.TicketID)}>
                  {t.ComplaintDescription} - {t.Remark} - {t.ResiType} - {t.ResidenceID} - {t.EmpName} - - {t.Mobile} - ({t.TicketID})
                </option>
              ))}
            </select>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-sm-6">
              <label className="form-label">Progress</label>
              <select
                className="form-select"
                name="progressId"
                value={form.progressId}
                onChange={onChange}
                required
              >
                {progressTypes.map(p => (
                  <option key={p.ProgressID} value={String(p.ProgressID)}>{p.ProgressDetails}</option>
                ))}
              </select>
            </div>

            <div className="col-sm-4">
              <label className="form-label">Transferred To</label>
              <select
                className="form-select"
                name="resolverId"
                value={form.resolverId}
                onChange={onChange}
                required
              >
                {resolverMasters.map(r => (
                  <option key={r.ResolverID} value={String(r.ResolverID)}>{r.Resolver}</option>
                ))}
              </select>
            </div>

            <div className="col-sm-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={form.status}
                onChange={onChange}
                required
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Remarks</label>
            <input
              className="form-control"
              type="text"
              name="remarks"
              value={form.remarks}
              onChange={onChange}
              placeholder="Description...*"
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Supporting Document (JPG, JPEG, PNG, PDF — max 200 KB, images auto-reduced)</label>
            <input
              className="form-control"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              onChange={onDocChange}
              disabled={docProcessing}
            />
            {docProcessing && <div className="form-text">Reducing image size…</div>}
            {docInfo && !docError && <div className="form-text text-success">{docInfo}</div>}
            {docError && <div className="form-text text-danger">{docError}</div>}
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading || pendingTickets.length === 0}>
            {loading ? "Saving..." : "Save Now"}
          </button>

        </div>
      </form>
    </div>
  );
}
